import React, { useMemo } from "react";

import { Axis, AxisRight } from "@visx/axis";
import { localPoint } from "@visx/event";
import { LinearGradient } from "@visx/gradient";
import { GridRows } from "@visx/grid";
import { Group } from "@visx/group";
import { ParentSize, ScaleSVG } from "@visx/responsive";
import { scaleLinear, scaleTime } from "@visx/scale";
import { Circle, Line } from "@visx/shape";
import { Tooltip } from "@visx/tooltip";
import { UseTooltipParams } from "@visx/tooltip/lib/hooks/useTooltip";

import styled from "@mui/material/styles/styled";

import { TimeSeriesData } from "@app/common/types";
import { formatTimeTick } from "@app/common/utils/datetime";

import Stack from "@rui/Stack";

import TimeSeriesCurve from "./TimeSeriesCurve";

const MS_PER_MIN = 60000;
const MAX_NUM_X_TICKS = 7;

const TOOLTIP_OFFSET = {
  right: 4, // offset for aligning the tooltip on the right side of the line
  left: -8, // offset for aligning the tooltip on the left side of the line
};

export const SPACING = {
  top: 14, // spacing for the top y-axis tick value
  right: 30, // space for the y-axis tick values
  bottom: 50, // space for the x-axis tick values
  xAxis: 16, // space between the axis and the axis labels
};

const COMMON_TICK_LABEL_PROPS = {
  fontFamily: "Work Sans",
  fontSize: "14px",
  fill: "#848484",
  fontWeight: 500,
};

interface TooltipDataSet {
  id: string;
  rawValue: number;
  value: string | number | string[];
  color: React.CSSProperties["stroke"];
}

export interface MultipleTimeSeriesTooltipData {
  timestamp?: string;
  indicators?: TooltipDataSet[];
  additionalData?: Pick<TooltipDataSet, "id" | "value">[];
}

export interface SingleTimeSeriesData {
  id: string;
  data: TimeSeriesData[];
  color: React.CSSProperties["stroke"];
}

export interface MultipleTimeSeriesChartParentProps {
  series: SingleTimeSeriesData[];
  /** [min, max] of possible x-axis values */
  domain: [number, number];
  height?: number;
  /** [min, max] of possible y-axis values */
  range: [number, number];
  /** Step size of the y-axis */
  rangeStep: number;
  renderTooltip?: (
    tooltipData: MultipleTimeSeriesTooltipData
  ) => React.ReactElement;
  tooltip?: UseTooltipParams<MultipleTimeSeriesTooltipData> & {
    onTooltipUpdate: (x: number, timestamp: number) => void;
    width: number;
  };
}

interface MultipleTimeSeriesChartProps
  extends MultipleTimeSeriesChartParentProps {
  height: number;
  width: number;
}

const getYAxisTicks = (
  range: MultipleTimeSeriesChartProps["range"],
  rangeStep: MultipleTimeSeriesChartProps["rangeStep"]
): number[] => {
  const [firstStep, lastStep] = range;
  const tickValues = [firstStep];
  let nextStep = Math.min(firstStep + rangeStep, lastStep);
  while (nextStep < lastStep) {
    tickValues.push(nextStep);
    nextStep = Math.min(nextStep + rangeStep, lastStep);
  }
  tickValues.push(lastStep);
  return tickValues;
};

const getXAxisTicks = (
  domain: MultipleTimeSeriesChartProps["domain"]
): number[] => {
  const [startMs, endMs] = domain;
  const tickValues: number[] = [0];
  let tickIntervalInMinutes = 5;

  const durationInMs = Math.max(endMs - startMs, 0);
  if (durationInMs > 30 * MS_PER_MIN || durationInMs < 10 * MS_PER_MIN) {
    // for durations < 10 minutes or > 30 minutes, we want the tick intervals
    // to be based on the data and the maximum number of ticks that fit visually
    tickIntervalInMinutes = Math.ceil(
      durationInMs / MAX_NUM_X_TICKS / MS_PER_MIN
    );
  }

  const tickIntervalInMs = tickIntervalInMinutes * MS_PER_MIN;
  const numTicks = Math.floor(durationInMs / tickIntervalInMs);

  for (let i = 0; i < numTicks; i++) {
    tickValues.push(tickValues[i] + tickIntervalInMs);
  }

  return tickValues;
};

const TooltipBox = styled(Stack)(({ theme }) => ({
  backgroundColor: theme.palette.overlay.primary[4],
  color: "#FFF",
  borderRadius: "4px",
  boxShadow: "0px 8px 24px rgba(0, 0, 0, 0.1)",
}));

const MultipleTimeSeriesChart = ({
  domain,
  height,
  range,
  rangeStep,
  renderTooltip,
  series,
  tooltip,
  width,
}: MultipleTimeSeriesChartProps) => {
  const chartGridHeight = Math.max(0, height - SPACING.bottom);
  const chartGridWidth = Math.max(0, width - SPACING.right);

  const xScale = useMemo(
    () =>
      scaleTime<number>({
        domain,
        range: [0, chartGridWidth],
      }),
    [chartGridWidth, domain]
  );

  const [start, end] = domain;
  const durationInMs = Math.max(0, end - start);

  const xAxisScale = useMemo(
    () =>
      scaleLinear<number>({
        domain: [0, durationInMs],
        range: [0, chartGridWidth],
      }),
    [durationInMs, chartGridWidth]
  );

  const yScale = useMemo(
    () =>
      scaleLinear<number>({
        domain: range,
        range: [chartGridHeight, 0],
      }),
    [chartGridHeight, range]
  );

  const yAxisTickValues = getYAxisTicks(range, rangeStep);
  const xAxisTickValues = getXAxisTicks(domain);

  const colorsById = series.reduce(
    (colors, data) => {
      colors[data.id] = {
        id: data.id,
        gradientId: `multiple-time-series-area-gradient-${data.id}`,
        color: data.color,
      };
      return colors;
    },
    {} as {
      [key: string]: {
        id: string;
        gradientId: string;
        color: React.CSSProperties["stroke"];
      };
    }
  );

  const handleMouseMove = (event: React.MouseEvent) => {
    if (!tooltip) return;

    const point = localPoint(event);
    const x = point?.x ?? 0;
    const timestampInMs = xScale.invert(x).getTime();
    tooltip.onTooltipUpdate(x, timestampInMs);
  };

  const showTooltip =
    tooltip != null && tooltip?.tooltipOpen && tooltip?.tooltipData != null;
  const tooltipLeft = tooltip?.tooltipLeft ?? 0;
  const invertTooltipDirection =
    tooltipLeft + TOOLTIP_OFFSET.right + (tooltip?.width ?? 0) > chartGridWidth;
  const tooltipTopOffset = chartGridHeight * 0.25;

  return (
    <Stack sx={{ position: "relative" }}>
      <ScaleSVG height={height} width={width} preserveAspectRatio="none">
        {Object.values(colorsById).map((color) => (
          <LinearGradient
            key={color.gradientId}
            id={color.gradientId}
            from={color.color}
            to={color.color}
            fromOpacity="20%"
            toOpacity="0%"
          />
        ))}
        <Axis
          scale={xAxisScale}
          left={0}
          hideAxisLine
          hideTicks
          top={chartGridHeight + SPACING.top}
          numTicks={xAxisTickValues.length}
          tickLabelProps={() => ({
            ...COMMON_TICK_LABEL_PROPS,
            textAnchor: "start",
            dy: SPACING.xAxis,
          })}
          tickValues={xAxisTickValues}
          tickFormat={formatTimeTick}
        />
        <GridRows
          scale={yScale}
          top={SPACING.top}
          left={0}
          height={chartGridHeight}
          width={chartGridWidth}
          tickValues={yAxisTickValues}
          strokeWidth={2}
          stroke="#E0E0E0"
        />
        <Group top={SPACING.top} left={0}>
          <AxisRight
            scale={yScale}
            stroke="#E0E0E0"
            tickValues={yAxisTickValues}
            left={width - SPACING.right}
            hideAxisLine
            hideTicks
            tickLabelProps={() => ({
              ...COMMON_TICK_LABEL_PROPS,
              verticalAnchor: "middle",
            })}
          />
          {series.map(({ id, data, color }) => (
            <TimeSeriesCurve
              key={color}
              data={data}
              fill={`url(#${colorsById[id].gradientId})`}
              strokeColor={color}
              xScale={xScale}
              yScale={yScale}
            />
          ))}
          {showTooltip && (
            <>
              <Line
                from={{ x: tooltipLeft, y: 0 }}
                to={{ x: tooltipLeft, y: chartGridHeight }}
                stroke="#A3A3A3"
                strokeWidth={1}
              />
              {tooltip?.tooltipData?.indicators?.map((indicator) => (
                <Circle
                  key={indicator.id}
                  cx={tooltip?.tooltipLeft}
                  cy={yScale(indicator.rawValue)}
                  r={3}
                  stroke={indicator.color}
                  fill="#FFF"
                  strokeWidth={2}
                />
              ))}
            </>
          )}
          {/* Tooltip hover area */}
          <rect
            width={chartGridWidth}
            height={chartGridHeight}
            y={0}
            x={0}
            onMouseMove={handleMouseMove}
            onMouseLeave={tooltip?.hideTooltip}
            fill="transparent"
          />
        </Group>
      </ScaleSVG>
      {showTooltip && (
        <Tooltip
          left={
            invertTooltipDirection
              ? tooltipLeft - tooltip?.width
              : tooltipLeft + TOOLTIP_OFFSET.right
          }
          top={tooltipTopOffset}
          offsetLeft={
            invertTooltipDirection ? TOOLTIP_OFFSET.left : TOOLTIP_OFFSET.right
          }
          style={{ position: "absolute", pointerEvents: "none", zIndex: 1 }}
        >
          <TooltipBox>{renderTooltip?.(tooltip!.tooltipData!)}</TooltipBox>
        </Tooltip>
      )}
    </Stack>
  );
};

const TimeSeriesChartParent = (props: MultipleTimeSeriesChartParentProps) => (
  <ParentSize>
    {({ width, height }) => (
      <MultipleTimeSeriesChart
        width={Math.max(0, width)}
        height={Math.max(0, height)}
        {...props}
      />
    )}
  </ParentSize>
);

export default TimeSeriesChartParent;
